import { useState } from "react";
import { Pencil, Trash2, X, Check } from "lucide-react";
import { RecurringFrequency, type RecurringIncomeItem } from "@boo/shared";
import { useRecurringIncomeStore } from "../../store/useRecurringIncomeStore";

interface Props {
  item: RecurringIncomeItem;
}

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatBaht(n: number) {
  return `฿${n.toLocaleString("en-US")}`;
}

function frequencyLabel(item: RecurringIncomeItem): string {
  if (item.frequency === RecurringFrequency.YEARLY) {
    return `yearly · ${MONTH_ABBR[(item.applyMonth ?? 1) - 1]}`;
  }
  return "monthly";
}

export default function RecurringIncomeRow({ item }: Props) {
  const [confirming, setConfirming] = useState(false);
  const { openEditModal, removeItem } = useRecurringIncomeStore();

  return (
    <div className="grid items-center gap-3.5 px-4 py-3 rounded-[10px] grid-cols-[1fr_auto] bg-[var(--surface-muted)] border-[0.5px] border-border/50">
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="text-[13px] font-medium truncate text-foreground">
          {item.title}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {frequencyLabel(item)}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[14px] font-bold tabular-nums text-[var(--color-income)] [font-variant-numeric:tabular-nums]">
          +{formatBaht(item.amount)}
        </span>

        {confirming ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setConfirming(false)}
              className="p-2 sm:p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-card transition-colors cursor-pointer"
              aria-label="Cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => removeItem(item.id)}
              className="p-2 sm:p-1 rounded-md text-red-500 hover:text-red-600 hover:bg-card transition-colors cursor-pointer"
              aria-label="Confirm delete"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={() => openEditModal(item)}
              className="p-2 sm:p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-card transition-colors cursor-pointer"
              aria-label="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setConfirming(true)}
              className="p-2 sm:p-1 rounded-md text-muted-foreground hover:text-red-500 hover:bg-card transition-colors cursor-pointer"
              aria-label="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
